// react
import {
	ChangeEvent,
	Dispatch,
	MouseEvent,
	SetStateAction,
	useContext,
	useEffect,
	useState,
} from 'react';

// types
import {
	Account,
	AccountBalancePayload,
	AccountEntityPayload,
	AccountPayload,
	AccountType,
	CommentPayload,
	Entity,
	ErrorCause,
	ExchangeRateByDate,
	Loan,
} from '../../../@types';

// i18n
import { useTranslation } from 'react-i18next';

// config
import { featureFlags } from '../../../config/featureFlags';

// router
import { useSearchParams } from 'react-router-dom';

// context
import { ContextBanner, ContextUser, ContextUserEntity } from '../../../App.context';
import { ContextProject, ContextUserProjectPermissions } from '../../pages/Project/Project.context';

// hooks
import { useExchangeRates } from '../../../hooks/useExchangeRates';
import { useFetch } from '../../../hooks/useFetch';

// mui
import { Alert, Box, Grid, SelectChangeEvent, Typography } from '@mui/material';

// icons
import { FlagOutlined as IconFlag } from '@mui/icons-material';

// img
import { LogoWorldBankMark } from '../../img/LogoWorldBankMark';

// fields
import { ExchangeRates } from '../../fields/ExchangeRates/ExchangeRates';
import { Input } from '../../fields/Input/Input';
import { RadioButtons } from '../../fields/RadioButtons/RadioButtons';
import { Select } from '../../fields/Select/Select';

// ui
import { ListItemTextAccount } from '../../ui/ListItemTextAccount/ListItemTextAccount';

// utils
import { translateType } from '../../../utils/translations';
import { Amounts } from '../../fields/Amounts/Amounts';
import { SelectCountry } from '../../fields/SelectCountry/SelectCountry';
import { SelectCurrency } from '../../fields/SelectCurrency/SelectCurrency';
import { Checkbox } from '../../fields/Checkbox/Checkbox';

// props
export interface FormAddAccountValues {
	aba: string;
	accounts: number[];
	amounts: Record<string, number>;
	country: string;
	currency: string;
	entities: number[];
	holder: string;
	institution: string;
	isReimbursement: boolean;
	name: string;
	num: string;
	owner: string;
	postcode: string;
	routeCode: string;
	sortCode: string;
	state: string;
	street: string;
	swiftCode: string;
	town: string;
	type: AccountType;
}

export interface FormAddAccountProps {
	onClose?: () => void;
	onSuccess?: () => void;
	setIsLoading: Dispatch<SetStateAction<boolean>>;
}

export const FormAddAccount = ({ onClose, onSuccess, setIsLoading }: FormAddAccountProps) => {
	// hooks
	const { t } = useTranslation();

	// search params
	const [searchParams] = useSearchParams();

	// params
	const paramDialogAccountName = searchParams.get('dialogAccountName');
	const paramDialogInstitution = searchParams.get('dialogInstitution');
	const paramDialogRouteCode = searchParams.get('dialogRouteCode');
	const paramDialogSwiftCode = searchParams.get('dialogSwiftCode');
	const paramDialogType = searchParams.get('dialogType');

	// defaults
	const defaultType = paramDialogType || 'DESIGNATED';

	// state
	const [accounts, setAccounts] = useState<Account[]>([]);
	const [entities, setEntities] = useState<Entity[]>([]);
	const [error, setError] = useState<string | null>(null);
	const [exchangeRates, setExchangeRates] = useState<ExchangeRateByDate[]>([]);
	const [loans, setLoans] = useState<Loan[]>([]);
	const [values, setValues] = useState<FormAddAccountValues>({
		aba: '',
		accounts: [],
		amounts: {},
		country: '',
		currency: 'USD',
		entities: [],
		holder: '',
		institution: paramDialogInstitution || '',
		isReimbursement: false,
		name: paramDialogAccountName || '',
		num: '',
		owner: '',
		postcode: '',
		routeCode: paramDialogRouteCode || '',
		sortCode: '',
		state: '',
		street: '',
		swiftCode: paramDialogSwiftCode || '',
		town: '',
		type: defaultType as AccountType,
	});

	// context
	const { setBanner } = useContext(ContextBanner);
	const { project } = useContext(ContextProject);
	const { user } = useContext(ContextUser);
	const { userEntity } = useContext(ContextUserEntity);
	const { userProjectPermissions } = useContext(ContextUserProjectPermissions);

	// hooks
	const { getCurrenciesForExchangeRates } = useExchangeRates();

	// vars
	const formId = 'form-add-account';
	const isDesignated = values.type === 'DESIGNATED';
	const isLocal = values.type === 'LOCAL';

	// session
	const accessToken = sessionStorage.getItem('accessToken');
	const idToken = sessionStorage.getItem('idToken');

	// fetch accounts
	useFetch({
		isEnabled: Boolean(project),
		url: `${process.env.REACT_APP_API_URL}/accounts?project=${project?.id}&populate=loan&type=DESIGNATED`,
		options: {
			headers: {
				Authorization: `Bearer ${accessToken}`,
				User: String(idToken),
			},
		},
		onSuccess: (res) => {
			setAccounts(res.data);
		},
	});

	// fetch loans
	useFetch({
		isEnabled: Boolean(project),
		url: `${process.env.REACT_APP_API_URL}/loans?project=${project?.id}&fields=id,code,financier,status`,
		options: {
			headers: {
				Authorization: `Bearer ${accessToken}`,
				User: String(idToken),
			},
		},
		onSuccess: (res) => {
			if (res.data) {
				setLoans(res.data);
			}
		},
	});

	// fetch entities
	useFetch({
		isEnabled: Boolean(project),
		url: `${process.env.REACT_APP_API_URL}/entities?project=${project?.id}&fields=id,name,type,wallet&type=BORROWER,INTERMEDIARY,PIU,SUB_PIU&populate=wallet`,
		options: {
			headers: {
				Authorization: `Bearer ${accessToken}`,
				User: String(idToken),
			},
		},
		onSuccess: (res) => {
			setEntities(res.data);
		},
	});

	// options
	const optionsAccounts = accounts.map((account) => {
		return {
			label: <ListItemTextAccount account={account} />,
			secondary: account.institution,
			value: account.id,
			MenuItemProps: {
				resource: account,
			},
		};
	});

	const optionsEntities = entities
		.map((entity) => {
			return {
				disabled: entity.wallet?.status === 'INACTIVE',
				label: entity.name,
				secondary: translateType(entity.type, t),
				value: String(entity.id),
			};
		})
		.sort((a, b) => (a.label.toLowerCase() > b.label.toLowerCase() ? 1 : -1));

	// filter out owner for additional entities
	const optionsEntitiesAdditional = optionsEntities.filter(
		(entity) => entity.value !== values.owner
	);

	const handleChangeValue = (e: ChangeEvent | SelectChangeEvent<unknown>) => {
		const { name, value } = e.target as HTMLInputElement;
		setValues({
			...values,
			[name]: value,
		});
	};

	const handleChangeExchangeRates = (index: number, name: string, value: string | number) => {
		const exchangeRatesNew = [...exchangeRates];
		exchangeRatesNew[index] = {
			...exchangeRatesNew[index],
			[name]: value,
		};
		setExchangeRates(exchangeRatesNew);
	};

	const handleSubmit = async (e: MouseEvent<HTMLFormElement>) => {
		e.preventDefault();

		const form = document.querySelector(`#${formId}`) as HTMLFormElement;
		const fd = new FormData(form);
		const entries = Object.fromEntries(fd.entries());

		try {
			// clear error
			setError(null);

			// set loading
			setIsLoading(true);

			// define entities
			let entities: AccountEntityPayload[] = [];

			if (values.entities) {
				entities = values.entities.map((id) => {
					return {
						id: Number(id),
						relationship: 'ASSOCIATE',
					};
				});
			}

			// check if OWNER entity is associated entity
			const ownerFound = entities.find((entity) => entity.id === Number(values.owner));

			if (ownerFound) {
				throw new Error('The account owner cannot be an associated entity.', {
					cause: {
						id: 'ACCOUNT_OWNER_ASSOCIATE',
					},
				});
			}

			// add owner entity
			entities.push({
				id: Number(values.owner),
				relationship: 'OWNER',
			});

			// define accounts
			let accounts: number[] = [];
			if (values.accounts) {
				accounts = values.accounts.map((id) => Number(id));
			}

			// define balances using loans
			const balances: AccountBalancePayload[] = [];

			for (let s = 0; s < loans.length; s++) {
				if (entries[`source-${s}-amount`]) {
					// Define conversions using exchange rates
					const conversions = exchangeRates.map((exchangeRate, indexExchangeRate) => {
						const conversionAmount =
							Number(entries[`source-${s}-amount-conversion-${indexExchangeRate}`]) || 0;
						const conversionTax =
							Number(entries[`source-${s}-tax-conversion-${indexExchangeRate}`]) || 0;

						return {
							amount: conversionAmount,
							currency: exchangeRate.currency,
							dateExchangeRate: exchangeRate.dateExchangeRate,
							exchangeRate: exchangeRate.exchangeRate,
							tax: conversionTax,
						};
					});

					balances.push({
						balance: Number(entries[`source-${s}-amount`]),
						conversions,
						loan: loans[s].id,
					});
				}
			}

			if (!balances.length && !values.isReimbursement) {
				throw new Error('At least one balance must be defined.', {
					cause: {
						id: 'ACCOUNT_BALANCES_NON_EXISTENT',
					},
				});
			}

			const body: AccountPayload = {
				accounts,
				balances,
				currency: String(entries.currency),
				entities,
				institution: String(values.institution),
				isReimbursement: values.isReimbursement,
				name: String(values.name),
				num: String(values.num),
				project: project?.id,
				type: values.type,
			};

			if (values.aba) {
				body.aba = values.aba;
			}

			if (values.country) {
				body.country = Number(values.country);
			}

			if (values.holder) {
				body.holder = values.holder;
			}

			if (values.postcode) {
				body.postcode = values.postcode;
			}

			if (values.routeCode) {
				body.routeCode = values.routeCode;
			}

			if (values.sortCode) {
				body.sortCode = values.sortCode;
			}

			if (values.state) {
				body.state = values.state;
			}

			if (values.street) {
				body.street = values.street;
			}

			if (values.swiftCode) {
				body.swiftCode = values.swiftCode;
			}

			if (values.town) {
				body.town = values.town;
			}

			if (accounts.length) {
				body.accounts = accounts;
			}

			const fetchAddAccount = await fetch(`${process.env.REACT_APP_API_URL}/accounts`, {
				method: 'post',
				body: JSON.stringify(body),
				headers: {
					Authorization: `Bearer ${accessToken}`,
					User: String(idToken),
				},
			});
			const resAddAccount = await fetchAddAccount.json();

			if (resAddAccount.error) {
				throw new Error(resAddAccount.error.message, {
					cause: {
						id: resAddAccount.error.id,
					},
				});
			}

			// post comments
			if (entries.comments && userProjectPermissions?.comment?.create) {
				const bodyComment: CommentPayload = {
					account: resAddAccount?.data?.id,
					comment: entries.comments as string,
				};

				if (user?.id) {
					bodyComment.user = user.id;
				}

				const fetchPostComment = await fetch(`${process.env.REACT_APP_API_URL}/comments`, {
					method: 'post',
					body: JSON.stringify(bodyComment),
					headers: {
						Authorization: `Bearer ${accessToken}`,
						User: String(idToken),
					},
				});

				const resPostComment = await fetchPostComment.json();

				if (resPostComment.error) {
					throw new Error(resPostComment.error.message, {
						cause: {
							id: resPostComment.error.id,
						},
					});
				}
			}

			if (onSuccess) {
				onSuccess();
			}

			// set success banner
			setBanner({
				hasClose: true,
				message: t('alert.accountCreated', { name: values.name }),
				severity: 'success',
			});

			// close dialog
			if (onClose) {
				onClose();
			}
		} catch (error) {
			const err = error as Error;
			const cause = err.cause as ErrorCause;

			let msg = t('error.default');

			if (cause) {
				if (cause.id === 'ACCOUNT_BALANCES_NON_EXISTENT') {
					msg = t('error.accountBalancesNonExistent');
				}

				if (cause.id === 'ACCOUNT_OWNER_ASSOCIATE') {
					msg = t('error.accountOwnerAssociate');
				}

				if (cause.id === 'ACCOUNT_UNAUTHORIZED_POST') {
					msg = t('error.unauthorized');
				}
			}

			// set error
			setError(msg);
		} finally {
			// set loading
			setIsLoading(false);
		}
	};

	// set user entity as owner
	useEffect(() => {
		if (userEntity) {
			setValues((valuesPrev) => {
				return {
					...valuesPrev,
					owner: String(userEntity.id),
				};
			});
		}
	}, [userEntity]);

	// initialize exchange rates when currency changes
	useEffect(() => {
		if (featureFlags['IFRS'] && values.currency) {
			const currenciesForExchangeRates = getCurrenciesForExchangeRates({
				currency: values.currency,
			});

			const exchangeRatesRequired = currenciesForExchangeRates.map((currency) => ({
				currency,
				dateExchangeRate: '',
				exchangeRate: 0,
			}));

			setExchangeRates(exchangeRatesRequired);
		}
	}, [getCurrenciesForExchangeRates, values.currency]);

	return (
		<form id={`${formId}`} name="formAddAccount" onSubmit={handleSubmit}>
			<Grid container spacing={2}>
				<Grid item xs={12}>
					<RadioButtons
						defaultValue={defaultType}
						id={`${formId}-type`}
						inputs={[
							{
								icon: <LogoWorldBankMark width={20} />,
								label: t('designatedAccount'),
								value: 'DESIGNATED',
							},
							{
								icon: <IconFlag />,
								label: t('localAccount'),
								value: 'LOCAL',
							},
						]}
						label={t('type')}
						name="type"
						onChange={(e, value) => {
							setValues((valuesPrev) => {
								return {
									...valuesPrev,
									type: value as AccountType,
								};
							});
						}}
						required={true}
					/>
				</Grid>
				{!isDesignated && (
					<Grid item xs={12}>
						<Checkbox
							id={`${formId}-is-reimbursement`}
							checked={values.isReimbursement}
							label={t('reimbursementAccount')}
							name="isReimbursement"
							onChange={() => {
								setValues((valuesPrev) => {
									return {
										...valuesPrev,
										isReimbursement: !valuesPrev.isReimbursement,
									};
								});
							}}
						/>
					</Grid>
				)}
				<Grid item xs={12} md={6}>
					<Input
						id={`${formId}-name`}
						label={t('name')}
						name="name"
						onChange={handleChangeValue}
						required={true}
						value={values.name}
					/>
				</Grid>
				<Grid item xs={12} md={6}>
					<Input
						id={`${formId}-number`}
						label={t('accountNumber')}
						name="num"
						onChange={handleChangeValue}
						required={true}
						value={values.num}
					/>
				</Grid>
				{isDesignated && (
					<Grid item xs={12}>
						<Input
							id={`${formId}-holder`}
							label={t('accountHolder')}
							name="holder"
							onChange={handleChangeValue}
							required={true}
							value={values.holder}
						/>
					</Grid>
				)}
				<Grid item xs={12} md={12}>
					<Box
						sx={(theme) => {
							return {
								backgroundColor: theme.palette.brand.grey[200],
								borderRadius: 2,
								padding: theme.spacing(2),
							};
						}}>
						<Grid container spacing={2}>
							<Grid item xs={12} md={6}>
								<Input
									id={`${formId}-institution`}
									label={t('financialInstitution')}
									name="institution"
									onChange={handleChangeValue}
									required={true}
									value={values.institution}
								/>
							</Grid>
							<Grid item xs={12} md={6}>
								<Input
									id={`${formId}-street`}
									label={t('street')}
									name="street"
									onChange={handleChangeValue}
									required={true}
									value={values.street}
								/>
							</Grid>
							<Grid item xs={12} md={6}>
								<SelectCountry
									id={`${formId}-country`}
									label={t('country')}
									name="country"
									onChange={handleChangeValue}
									required={true}
									value={values.country}
									valueType="ID"
								/>
							</Grid>
							<Grid item xs={12} md={6}>
								<Input
									id={`${formId}-town`}
									label={t('town')}
									name="town"
									onChange={handleChangeValue}
									required={true}
									value={values.town}
								/>
							</Grid>
							<Grid item xs={12} md={6}>
								<Input
									id={`${formId}-state`}
									label={t('state')}
									name="state"
									onChange={handleChangeValue}
									value={values.state}
								/>
							</Grid>
							<Grid item xs={12} md={6}>
								<Input
									id={`${formId}-postcode`}
									label={t('postcode')}
									name="postcode"
									onChange={handleChangeValue}
									required={true}
									value={values.postcode}
								/>
							</Grid>
							<Grid item xs={12} md={6}>
								<Input
									id={`${formId}-swift-code`}
									label={t('swiftCode')}
									name="swiftCode"
									onChange={handleChangeValue}
									required={true}
									value={values.swiftCode}
								/>
							</Grid>
							<Grid item xs={12} md={6}>
								<Input
									id={`${formId}-aba`}
									label={t('aba')}
									name="aba"
									onChange={handleChangeValue}
									value={values.aba}
								/>
							</Grid>
							<Grid item xs={12} md={6}>
								<Input
									id={`${formId}-route-code`}
									label={t('routeCode')}
									name="routeCode"
									onChange={handleChangeValue}
									value={values.routeCode}
								/>
							</Grid>
							<Grid item xs={12} md={6}>
								<Input
									id={`${formId}-sort-code`}
									label={t('sortCode')}
									name="sortCode"
									onChange={handleChangeValue}
									value={values.sortCode}
								/>
							</Grid>
						</Grid>
					</Box>
				</Grid>
				{isLocal && (
					<Grid item xs={12}>
						<Select
							id={`${formId}-designated-accounts`}
							label={t('designatedAccounts')}
							multiple={true}
							name="accounts"
							onChange={handleChangeValue}
							options={optionsAccounts}
							value={values.accounts}
						/>
					</Grid>
				)}

				<Grid item xs={12}>
					<Select
						hasNoneOption={false}
						id={`${formId}-owner`}
						label="Owner"
						name="owner"
						onChange={handleChangeValue}
						options={optionsEntities}
						required={true}
						value={values.owner}
					/>
				</Grid>

				{optionsEntitiesAdditional.length > 0 && (
					<Grid item xs={12}>
						<Select
							disabled={values.owner === ''}
							hasNoneOption={false}
							helperText={t('additionalEntities.helper')}
							id={`${formId}-entities`}
							label={t('additionalEntities')}
							multiple={true}
							name="entities"
							onChange={handleChangeValue}
							options={optionsEntitiesAdditional}
							value={values.entities}
						/>
					</Grid>
				)}
				{values.isReimbursement && (
					<Grid item xs={12}>
						<SelectCurrency
							id={`${formId}-currency`}
							label={t('currency')}
							name="currency"
							onChange={handleChangeValue}
							required={true}
							value={values.currency}
						/>
					</Grid>
				)}
				{!values.isReimbursement && (
					<>
						{featureFlags['IFRS'] && exchangeRates.length > 0 && (
							<Grid item xs={12}>
								<ExchangeRates
									currencyBase={values.currency}
									exchangeRates={exchangeRates}
									onChange={handleChangeExchangeRates}
								/>
							</Grid>
						)}
						<Grid item xs={12}>
							<Amounts
								currency={values.currency}
								exchangeRates={exchangeRates}
								formId={formId}
								hasCurrency={!values.isReimbursement}
								loans={loans}
								onChange={(e, total) => {
									const amountsNew: FormAddAccountValues['amounts'] = { ...values.amounts };
									const indexSource = e.target.getAttribute('data-index');

									amountsNew[indexSource as string] = e.target.value as unknown as number;

									setValues((prevState) => {
										return {
											...prevState,
											amounts: amountsNew,
											total,
										};
									});
								}}
								onChangeCurrency={(e) => {
									setValues((prevState) => {
										return {
											...prevState,
											currency: e.target.value as string,
										};
									});
								}}
								isRequired={false}
								title={t('balances')}
							/>
						</Grid>
						<Grid item xs={12}>
							<Typography color="text.secondary" variant="caption">
								{t('balances.helper')}
							</Typography>
						</Grid>
					</>
				)}
				{userProjectPermissions?.comment?.create && (
					<Grid item xs={12}>
						<Input
							id={`${formId}-comments`}
							label={t('comments')}
							minRows={2}
							multiline={true}
							name="comments"
						/>
					</Grid>
				)}
				{error && (
					<Grid item xs={12}>
						<Alert severity="error">{error}</Alert>
					</Grid>
				)}
			</Grid>
		</form>
	);
};
